import { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { Paperclip, Upload, Download, Trash2, FileText, Image, FileSpreadsheet, File, Loader2, Mail, Eye, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { uploadAttachment, downloadAttachment, deleteAttachmentApi, createTextAttachment } from '@/hooks/useTickets';
import type { TicketAttachmentItem, TicketDetail } from '@uniticket/shared';

export const ATTACHMENT_ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.xlsx,.xls,.csv,.docx,.doc,.txt,.zip,.eml,.msg';
const ACCEPT = ATTACHMENT_ACCEPT;

// Keep in sync with MAX_TEXT_ATTACHMENT_BYTES in packages/api/src/services/attachment.service.ts
const MAX_TEXT_BYTES = 64 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function getFileIcon(mimeType: string) {
  if (mimeType === 'application/pdf') return <FileText className="w-4 h-4 text-red-500" />;
  if (mimeType.startsWith('image/')) return <Image className="w-4 h-4 text-blue-500" />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType === 'text/csv') {
    return <FileSpreadsheet className="w-4 h-4 text-green-600" />;
  }
  return <File className="w-4 h-4 text-slate-400" />;
}

export interface TicketAttachmentsHandle {
  triggerUpload: () => void;
  triggerPasteEmail: () => void;
}

interface Props {
  ticketId: string;
  attachments: TicketAttachmentItem[];
  onTicketUpdate: (ticket: TicketDetail) => void;
}

export const TicketAttachments = forwardRef<TicketAttachmentsHandle, Props>(function TicketAttachments({ ticketId, attachments, onTicketUpdate }, ref) {
  const { t } = useTranslation('tickets');
  const { user, isAdmin } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Paste-email modal
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteDraft, setPasteDraft] = useState('');
  const [pastingSubmit, setPastingSubmit] = useState(false);

  // View-full modal for text attachments
  const [viewing, setViewing] = useState<TicketAttachmentItem | null>(null);

  useImperativeHandle(ref, () => ({
    triggerUpload: () => fileInputRef.current?.click(),
    triggerPasteEmail: () => setPasteOpen(true),
  }));

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setUploading(true);
    try {
      const res = await uploadAttachment(ticketId, file);
      onTicketUpdate(res.data);
      toast.success(t('attachments.uploaded', { name: file.name }));
    } catch (err: any) {
      toast.error(err?.message || t('attachments.uploadError'));
    } finally {
      setUploading(false);
    }
  }

  async function handlePasteSubmit() {
    const text = pasteDraft.trim();
    if (!text) return;
    if (new Blob([text]).size > MAX_TEXT_BYTES) {
      toast.error(t('attachments.textTooLong'));
      return;
    }
    setPastingSubmit(true);
    try {
      const res = await createTextAttachment(ticketId, text);
      onTicketUpdate(res.data);
      setPasteOpen(false);
      setPasteDraft('');
      toast.success(t('attachments.pasteEmailSaved'));
    } catch (err: any) {
      toast.error(err?.message || t('attachments.pasteEmailError'));
    } finally {
      setPastingSubmit(false);
    }
  }

  async function handleDownload(attachment: TicketAttachmentItem) {
    setDownloadingId(attachment.id);
    try {
      const blob = await downloadAttachment(attachment.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err?.message || t('attachments.downloadError'));
    } finally {
      setDownloadingId(null);
    }
  }

  async function handleDelete(attachment: TicketAttachmentItem) {
    if (!window.confirm(t('attachments.deleteConfirm', { name: attachment.fileName }))) return;
    setDeletingId(attachment.id);
    try {
      const res = await deleteAttachmentApi(attachment.id);
      onTicketUpdate(res.data);
      toast.success(t('attachments.deleted', { name: attachment.fileName }));
    } catch (err: any) {
      toast.error(err?.message || t('attachments.deleteError'));
    } finally {
      setDeletingId(null);
    }
  }

  function canDelete(attachment: TicketAttachmentItem): boolean {
    return attachment.uploadedBy.id === user?.id || isAdmin;
  }

  function textPreview(content: string | null): string {
    if (!content) return '';
    const cleaned = content.replace(/\s+/g, ' ').trim();
    return cleaned.length > 120 ? cleaned.slice(0, 120) + '…' : cleaned;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-slate-400" />
          <h3 className="text-sm font-bold text-slate-800">
            {t('attachments.title')}{attachments.length > 0 ? ` (${attachments.length})` : ''}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPasteOpen(true)}
            disabled={pastingSubmit}
            className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            <Mail className="w-3 h-3" />
            {t('attachments.pasteEmail')}
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all disabled:opacity-50 flex items-center gap-1.5"
          >
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {uploading ? t('attachments.uploading') : t('attachments.upload')}
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          onChange={handleUpload}
          className="hidden"
        />
      </div>

      <div className="px-6 py-3">
        {attachments.length === 0 && !uploading && (
          <p className="text-sm text-slate-400 py-2 text-center">{t('attachments.noAttachments')}</p>
        )}

        {attachments.map((att) => {
          const isText = att.kind === 'TEXT';
          return (
            <div
              key={att.id}
              className="flex items-start gap-3 py-2.5 border-b border-slate-50 last:border-0 group"
            >
              <div className="shrink-0 mt-0.5">
                {isText
                  ? <Mail className="w-4 h-4 text-purple-500" />
                  : getFileIcon(att.mimeType)}
              </div>

              <div className="flex-1 min-w-0">
                {isText ? (
                  <>
                    <button
                      onClick={() => setViewing(att)}
                      className="text-sm font-medium text-slate-800 hover:text-brand-rose transition-colors text-left"
                    >
                      {t('attachments.textAttachmentLabel')} · {formatDate(att.createdAt)}
                    </button>
                    {att.textContent && (
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                        {textPreview(att.textContent)}
                      </p>
                    )}
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {formatFileSize(att.fileSize)} &middot; {att.uploadedBy.displayName}
                    </p>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleDownload(att)}
                      disabled={downloadingId === att.id}
                      className="text-sm font-medium text-slate-800 hover:text-brand-rose transition-colors truncate block max-w-full text-left"
                      title={att.fileName}
                    >
                      {att.fileName}
                    </button>
                    <p className="text-xs text-slate-400">
                      {formatFileSize(att.fileSize)} &middot; {att.uploadedBy.displayName} &middot; {formatDate(att.createdAt)}
                    </p>
                  </>
                )}
              </div>

              <div className="flex items-center gap-1 shrink-0">
                {isText && (
                  <button
                    onClick={() => setViewing(att)}
                    className="p-1.5 text-slate-300 hover:text-brand-rose transition-colors rounded"
                    title={t('attachments.viewFullText')}
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={() => handleDownload(att)}
                  disabled={downloadingId === att.id}
                  className="p-1.5 text-slate-300 hover:text-brand-rose transition-colors rounded"
                  title={isText ? t('attachments.downloadAsTxt') : t('attachments.download')}
                >
                  {downloadingId === att.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Download className="w-3.5 h-3.5" />
                  }
                </button>
                {canDelete(att) && (
                  <button
                    onClick={() => handleDelete(att)}
                    disabled={deletingId === att.id}
                    className="p-1.5 text-slate-300 hover:text-red-500 transition-colors rounded opacity-0 group-hover:opacity-100"
                    title={t('attachments.delete')}
                  >
                    {deletingId === att.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5" />
                    }
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {pasteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
            <div className="gradient-band px-6 py-4 flex items-center justify-between">
              <h3 className="text-sm font-bold text-white uppercase tracking-wide">
                {t('attachments.pasteEmailModalTitle')}
              </h3>
              <button
                onClick={() => { setPasteOpen(false); setPasteDraft(''); }}
                className="text-white/70 hover:text-white transition-colors"
                aria-label={t('attachments.pasteEmailCancel')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-3 max-h-[calc(100vh-8rem)] overflow-y-auto">
              <textarea
                rows={10}
                value={pasteDraft}
                onChange={(e) => setPasteDraft(e.target.value)}
                placeholder={t('attachments.pasteEmailPlaceholder')}
                className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono resize-y"
                autoFocus
              />
              <p className="text-xs text-slate-400 text-right">
                {formatFileSize(new Blob([pasteDraft]).size)} / {formatFileSize(MAX_TEXT_BYTES)}
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setPasteOpen(false); setPasteDraft(''); }}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  {t('attachments.pasteEmailCancel')}
                </button>
                <button
                  type="button"
                  onClick={handlePasteSubmit}
                  disabled={!pasteDraft.trim() || pastingSubmit}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all disabled:opacity-50 flex items-center gap-1.5"
                >
                  {pastingSubmit && <Loader2 className="w-3 h-3 animate-spin" />}
                  {t('attachments.pasteEmailSave')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 overflow-hidden flex flex-col max-h-[80vh]">
            <div className="gradient-band px-6 py-4 flex items-center justify-between">
              <h3 className="text-sm font-bold text-white uppercase tracking-wide truncate">
                {t('attachments.textAttachmentLabel')} · {formatDate(viewing.createdAt)}
              </h3>
              <button
                onClick={() => setViewing(null)}
                className="text-white/70 hover:text-white transition-colors"
                aria-label={t('attachments.pasteEmailCancel')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 overflow-auto flex-1">
              <pre className="text-sm text-slate-800 whitespace-pre-wrap break-words font-mono">
                {viewing.textContent}
              </pre>
            </div>
            <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => handleDownload(viewing)}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                {t('attachments.downloadAsTxt')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
